import { useOktaAuthContext } from '../contexts/OktaAuthContext.tsx';
import axios from 'axios';
//@ts-ignore
import qs from 'qs';
import { EventSource } from 'eventsource';
// BACK-END: https://api-int.mylaborelec.com/soiling-mitigation/ui/#

const URL_BASE = import.meta.env.VITE_BE_BASE_URL + 'soiling-mitigation/';
const genUserProfileURL = () => `${URL_BASE}user-profile`;
const genProjectsURL = () => `${URL_BASE}projects`;
const genProjectURL = (id: string) => `${URL_BASE}projects/${id}`;
const genSoilingAssessmentURL = () => `${URL_BASE}soiling/assessment`;
const genCleaningPackagesURL = () => `${URL_BASE}cleaning/packages`;
const genCleaningDetailsURL = () => `${URL_BASE}cleaning/details`;
const genMitigationStrategySessionURL = () => `${URL_BASE}mitigation-strategy-session`;
const genMitigationStrategyURL = () => `${URL_BASE}mitigation-strategy`;
const genOperationalStrategySessionURL = () => `${URL_BASE}operational-strategy-session`;
const genOperationalStrategyURL = () => `${URL_BASE}operational-strategy`;
const genOperationalStrategyExPostSessionURL = () => `${URL_BASE}ex-post-analysis-session`;
const genOperationalStrategyExPostURL = () => `${URL_BASE}ex-post-analysis`;
const genValidateCSVURL = () => `${URL_BASE}tools/validate-csv`;
const uploadSoilingSensorDataFILE = () => `${URL_BASE}files`;
const genSensorDataURL = () => `${URL_BASE}sensor-data`;

// Global Map to manage progress callbacks for different session types (outside of the hook)
const progressCallbacks = new Map<string, (progressMessage: string) => void>();

export const Server = () => {
  const { token } = useOktaAuthContext();

  const registerProgressCallback = (sessionType: string, callback: (progressMessage: string) => void) => {
    progressCallbacks.set(sessionType, callback);
  };

  const unregisterProgressCallback = (sessionType: string) => {
    progressCallbacks.delete(sessionType);
  };

  const getFromServer = async (url: string) => {
    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const getFromServerWithParams = async (url: string, params: any) => {
    const response = await axios.get(url, {
      params: params,
      paramsSerializer: (params) => {
        // Serializer needed to pass an array to fastapi, without the []
        return qs.stringify(params, { indices: false });
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const postToServerWithParams = async (url: string, params: any, body: any) => {
    const response = await axios.post(url, body, {
      params: params,
      paramsSerializer: (params) => {
        // Serializer needed to pass an array to fastapi, without the []
        return qs.stringify(params, { indices: false });
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const postToServer = async (url: string, payload: any) => {
    const response = await axios.post(url, payload, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const patchToServer = async (url: string, payload: any) => {
    const response = await axios.patch(url, payload, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const putToServer = async (url: string, payload: any) => {
    const response = await axios.put(url, payload, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const deleteToServer = async (url: string) => {
    const response = await axios.delete(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const postBLOBToServer = async (url: string, payload: any) => {
    const response = await axios.post(url, payload, {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const postFileToServer = async (url: string, file: any, params: any) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(url, formData, {
      params: params,
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  };

  const getUserProfileAPI = (): Promise<any> => {
    return getFromServer(genUserProfileURL());
  };

  const getProjectsAPI = () => {
    return getFromServer(genProjectsURL());
  };

  const getProjectAPI = (id: string) => {
    return getFromServer(genProjectURL(id));
  };

  const postProjectAPI = (payload: object) => {
    return postToServer(genProjectsURL(), payload);
  };

  const putProjectAPI = (id: string, payload: object) => {
    return putToServer(genProjectURL(id), payload);
  };

  const deleteProjectAPI = (id: string) => {
    return deleteToServer(genProjectURL(id));
  };

  const postSoilingAssessmentAPI = (
    params: {
      location: { latitude: number; longitude: number };
      cleaning_efficiency: number;
      rain_efficiency: number;
      yearly_cleaning_days: string;
      surface_tilt: number;
    },
    body: any,
  ) => {
    return postToServerWithParams(genSoilingAssessmentURL(), params, body);
  };

  const getCleaningPackagesAPI = () => {
    return getFromServer(genCleaningPackagesURL()).then((data) => {
      // Recursive function to remove null values
      const removeNullValues = (obj: any): any => {
        if (obj === null || obj === undefined || obj.length === 0) {
          return undefined;
        }

        if (Array.isArray(obj)) {
          return obj.map(removeNullValues).filter((item) => item !== undefined);
        }

        if (typeof obj === 'object') {
          const cleaned: any = {};
          for (const [key, value] of Object.entries(obj)) {
            const cleanedValue = removeNullValues(value);
            if (cleanedValue !== undefined) {
              cleaned[key] = cleanedValue;
            }
          }
          return Object.keys(cleaned).length > 0 ? cleaned : undefined;
        }

        return obj;
      };

      return removeNullValues(data);
    });
  };

  const postCleaningDetailsAPI = (formData: any) => {
    return postToServer(genCleaningDetailsURL(), formData);
  };

  const postMitigationStrategyAPI = (params: any, formData: any): Promise<any> => {
    return new Promise(async (resolve, reject) => {
      try {
        // Create a mitigation session by providing the input for the mitigation strategy
        const sessionResponse = await postToServerWithParams(genMitigationStrategySessionURL(), params, formData);
        const sessionId = sessionResponse.session_id;

        // Connect to the SSE endpoint by providing the session id
        const url = new URL(genMitigationStrategyURL());
        url.searchParams.append('session_id', sessionId);

        const eventSource = new EventSource(url.toString(), {
          fetch: (url, init) => {
            return fetch(url, {
              ...init,
              headers: {
                ...init.headers,
                Authorization: `Bearer ${token}`,
              },
            });
          },
        });

        let result: any = null;

        eventSource.addEventListener('start', (event) => {
          const data = JSON.parse(event.data);
        });

        eventSource.addEventListener('result', (event) => {
          result = JSON.parse(event.data);
          eventSource.close();
          resolve(result);
        });

        eventSource.addEventListener('progress', (event) => {
          const data = JSON.parse(event.data);
          // Use the registered callback for mitigation strategy progress
          const progressCallback = progressCallbacks.get('mitigation-strategy');
          if (progressCallback && data.progress !== undefined) {
            progressCallback(data.progress);
          }
        });

        eventSource.addEventListener('error', (event: MessageEvent) => {
          const data = JSON.parse(event.data);
          console.error('Analysis error:', data);
          eventSource.close();
          reject(new Error(`Analysis failed: ${data.message}`));
        });

        eventSource.onerror = (event) => {
          console.error('EventSource connection error:', event);
          eventSource.close();
          reject(new Error('EventSource connection failed'));
        };
      } catch (error) {
        console.error('Failed to create Mitigation strategy session:', error);
        reject(error);
      }
    });
  };

  const postOperationalStrategyAPI = (params: any, formData: any): Promise<any> => {
    return new Promise(async (resolve, reject) => {
      try {
        // Create a analysis session by providing the input for the analysis
        const sessionResponse = await postToServerWithParams(genOperationalStrategySessionURL(), params, formData);
        const sessionId = sessionResponse.session_id;

        // Connect to the SSE endpoint by providing the session id
        const url = new URL(genOperationalStrategyURL());
        url.searchParams.append('session_id', sessionId);

        const eventSource = new EventSource(url.toString(), {
          fetch: (url, init) => {
            return fetch(url, {
              ...init,
              headers: {
                ...init.headers,
                Authorization: `Bearer ${token}`,
              },
            });
          },
        });

        let result: any = null;

        eventSource.addEventListener('start', (event) => {
          const data = JSON.parse(event.data);
        });

        eventSource.addEventListener('result', (event) => {
          result = JSON.parse(event.data);
          eventSource.close();
          resolve(result);
        });

        eventSource.addEventListener('progress', (event) => {
          const data = JSON.parse(event.data);
          // Use the registered callback for operational strategy progress
          const progressCallback = progressCallbacks.get('operational-strategy');
          if (progressCallback && data.progress !== undefined) {
            progressCallback(data.progress);
          }
        });

        eventSource.addEventListener('error', (event: MessageEvent) => {
          const data = JSON.parse(event.data);
          console.error('Analysis error:', data);
          eventSource.close();
          reject(new Error(`Analysis failed: ${data.message}`));
        });

        eventSource.onerror = (event) => {
          console.error('EventSource connection error:', event);
          eventSource.close();
          reject(new Error('EventSource connection failed'));
        };
      } catch (error) {
        console.error('Failed to create analysis session:', error);
        reject(error);
      }
    });
  };

  const postOperationalStrategyExPostAPI = (params: any, formData: any): Promise<any> => {
    return new Promise(async (resolve, reject) => {
      try {
        // Create a analysis session by providing the input for the analysis
        const sessionResponse = await postToServerWithParams(genOperationalStrategyExPostSessionURL(), params, formData);
        const sessionId = sessionResponse.session_id;

        // Connect to the SSE endpoint by providing the session id
        const url = new URL(genOperationalStrategyExPostURL());
        url.searchParams.append('session_id', sessionId);

        const eventSource = new EventSource(url.toString(), {
          fetch: (url, init) => {
            return fetch(url, {
              ...init,
              headers: {
                ...init.headers,
                Authorization: `Bearer ${token}`,
              },
            });
          },
        });

        let result: any = null;

        eventSource.addEventListener('start', (event) => {
          const data = JSON.parse(event.data);
        });

        eventSource.addEventListener('result', (event) => {
          result = JSON.parse(event.data);
          eventSource.close();
          resolve(result);
        });

        eventSource.addEventListener('progress', (event) => {
          const data = JSON.parse(event.data);
          // Use the registered callback for ex-post progress
          const progressCallback = progressCallbacks.get('ex-post');
          if (progressCallback && data.progress !== undefined) {
            progressCallback(data.progress);
          }
        });

        eventSource.addEventListener('error', (event: MessageEvent) => {
          const data = JSON.parse(event.data);
          console.error('Analysis error:', data);
          eventSource.close();
          reject(new Error(`Analysis failed: ${data.message}`));
        });

        eventSource.onerror = (event) => {
          console.error('EventSource connection error:', event);
          eventSource.close();
          reject(new Error('EventSource connection failed'));
        };
      } catch (error) {
        console.error('Failed to create analysis session:', error);
        reject(error);
      }
    });
  };

  const postValidateCSVAPI = (file: any, params: any) => {
    return postFileToServer(genValidateCSVURL(), file, params);
  };

  const postSoilingSensorDataFileAPI = (file: any, params: any) => {
    return postFileToServer(uploadSoilingSensorDataFILE(), file, params);
  };

  const postSensorDataAPI = (
    params: { file_uid: string; file_type: string; project_id: string; latitude: number; longitude: number },
    body: any = {},
  ) => {
    return postToServerWithParams(genSensorDataURL(), params, body);
  };

  return {
    URL_BASE,
    getFromServerWithParams,
    getUserProfileAPI,
    getProjectsAPI,
    getProjectAPI,
    postProjectAPI,
    putProjectAPI,
    deleteProjectAPI,
    postSoilingAssessmentAPI,
    getCleaningPackagesAPI,
    postCleaningDetailsAPI,
    postMitigationStrategyAPI,
    postOperationalStrategyAPI,
    postOperationalStrategyExPostAPI,
    postValidateCSVAPI,
    postSoilingSensorDataFileAPI,
    postSensorDataAPI,
    postToServerWithParams,
    registerProgressCallback,
    unregisterProgressCallback,
  };
};
